import { TypedDocumentNode, gql } from '@apollo/client';
import { useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { useTypedParams } from '@sorare/routing';

import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Empty } from '@sorare/core/src/components/cards/Empty';
import { FOOTBALL_PLAY_PRO_FIXTURE_COMPETITION_CONTENDER_CONTENDER } from '@sorare/core/src/constants/__generated__/routes';
import usePaginatedQuery from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import useInfiniteScroll from '@sorare/core/src/hooks/useInfiniteScroll';
import { glossary } from '@sorare/core/src/lib/glossary';
import { mobileAndAbove } from '@sorare/core/src/style/mediaQuery';

import { getFixtureQueryVariables } from '@sorare/gameplay/src/lib/so5';

import { LineupLayout } from 'components/lineup/LineupLayout';
import { So5Lineup } from 'components/lineup/So5Lineup';

import {
  FootballProHistoryFixturePageQuery,
  FootballProHistoryFixturePageQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const PRO_HISTORY_FIXTURE_PAGE_QUERY = gql`
  query FootballProHistoryFixturePageQuery(
    $user: String!
    $slug: String
    $type: So5State
    $cursor: String
  ) {
    so5 {
      so5Fixture(slug: $slug, type: $type) {
        slug
        so5LeaderboardContenders(userSlug: $user, first: 20, after: $cursor) {
          nodes {
            slug
            so5Leaderboard {
              slug
              so5League {
                slug
              }
            }
            ...FootballLineupLayout_so5LeaderboardContender
            ...FootballSo5Lineup_so5LeaderboardContender
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
  ${LineupLayout.fragments.so5LeaderboardContender}
  ${So5Lineup.fragments.so5LeaderboardContender}
` as TypedDocumentNode<
  FootballProHistoryFixturePageQuery,
  FootballProHistoryFixturePageQueryVariables
>;

const Grid = styled.div`
  padding: 0 var(--double-unit);
  display: grid;
  gap: var(--triple-unit);
  @media ${mobileAndAbove} {
    grid-template-columns: repeat(
      auto-fill,
      minmax(clamp(340px, calc(${100 / 3}% - var(--double-unit)), 550px), 1fr)
        /** Between 340px and 1/3 and 550px to try to fit 3 items per columns */
    );
  }
`;

const HistoryFixturePage = () => {
  const { formatMessage } = useIntl();
  const { slug, fixture } = useTypedParams<PageParams>();
  const { data, loading, loadMore } = usePaginatedQuery(
    PRO_HISTORY_FIXTURE_PAGE_QUERY,
    {
      variables: {
        user: slug,
        ...getFixtureQueryVariables(fixture),
      },
      connection: 'So5LeaderboardContenderConnection',
    }
  );

  const { so5Fixture } = data?.so5 || {};
  const { so5LeaderboardContenders } = so5Fixture || {};

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, { cursor: so5LeaderboardContenders?.pageInfo.endCursor });
    },
    !!so5LeaderboardContenders?.pageInfo.hasNextPage,
    loading
  );

  const contenders = so5LeaderboardContenders?.nodes || [];

  if (loading) {
    return <LoadingIndicator fullHeight />;
  }

  return (
    <>
      {contenders.length === 0 && (
        <Empty
          title={formatMessage(glossary.quietWeek)}
          description={formatMessage(glossary.noTeamForThisWeek)}
        />
      )}
      <Grid>
        {contenders.map(contender => (
          <LineupLayout
            key={contender.slug}
            so5LeaderboardContender={contender}
            to={generatePath(
              FOOTBALL_PLAY_PRO_FIXTURE_COMPETITION_CONTENDER_CONTENDER,
              {
                fixture,
                competition: contender.so5Leaderboard.so5League.slug,
                contender: contender.slug,
              }
            )}
          >
            <So5Lineup so5LeaderboardContender={contender} />
          </LineupLayout>
        ))}
      </Grid>
      <InfiniteScrollLoader />
    </>
  );
};

export default HistoryFixturePage;
